import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { Button, Collapse, Alert } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import cn from 'classnames';

import styles from './page-error.module.less';
import { connect } from '../../../StateProvider';

const PageError = ({ error, company, user }) => {
  const { appVersion, apiVersion } = (company && typeof company.toJS === 'function' ? company.toJS() : {}) || {};
  const { id: userId } = (user && typeof user.toJS === 'function' ? user.toJS() : {}) || {};
  const currentDateTime = new Date().toLocaleString();
  const currentDomain = window.location.hostname;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [errorSent, setErrorSent] = useState(false);

  // Проверяем является ли ошибка ChunkLoadError
  const isChunkLoadError =
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('ChunkLoadError') ||
    error?.message?.includes('Loading chunk');

  useEffect(() => {
    if (error && !errorSent) {
      sendErrorToServer(error);
      setErrorSent(true);
    }
  }, [error, errorSent]);

  const sendErrorToServer = async (error) => {
    try {
      if (isLocalDevelopment()) {
        // eslint-disable-next-line no-console
        console.log('🔧 Dev mode: error not sent to server', error);
        return;
      }

      const normalizedError = typeof error === 'string' ? new Error(error) : error;

      const errorMessage = normalizedError.message || String(error);
      const errorStack = normalizedError.stack || errorMessage;
      const { pathname } = window.location;

      const stackLines = errorStack.split('\n');
      const shortStack = stackLines.slice(0, 5).join('\n');

      const browser = getBrowserInfo();

      const messageParts = [
        `🚨 Ошибка клиента: ${errorMessage}`,
        `🌍 Домен: ${currentDomain}`,
        `📍 Путь: ${pathname}`,
        `🕒 Время: ${new Date().toLocaleString('ru-RU')}`,
        `🌐 Браузер: ${browser}`,
      ];

      if (userId) messageParts.push(`👤 User ID: ${userId}`);
      if (appVersion) messageParts.push(`🛠 Версия: v${appVersion}`);

      messageParts.push('');
      messageParts.push(shortStack);

      let fullMessage = messageParts.join('\n');
      if (fullMessage.length > 1000) {
        fullMessage = `${fullMessage.substring(0, 997)}...`;
      }

      await fetch('https://admin.bpium.ru/api/webrequest/frontendErrors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: fullMessage,
        }),
      });
    } catch (err) {
      console.error('❌ Failed to send error to server:', err);
    }
  };

  const isLocalDevelopment = () => {
    const { hostname } = window.location;

    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.includes('.local')
    );
  };

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;

    if (ua.includes('Firefox/')) {
      const version = ua.match(/Firefox\/(\d+)/)?.[1];
      return `Firefox ${version}`;
    }
    if (ua.includes('Edg/')) {
      const version = ua.match(/Edg\/(\d+)/)?.[1];
      return `Edge ${version}`;
    }
    if (ua.includes('Chrome/')) {
      const version = ua.match(/Chrome\/(\d+)/)?.[1];
      return `Chrome ${version}`;
    }
    if (ua.includes('Safari/') && !ua.includes('Chrome')) {
      const version = ua.match(/Version\/(\d+)/)?.[1];
      return `Safari ${version}`;
    }

    return 'Unknown';
  };

  const reloadPage = () => location.reload();

  const copyError = async () => {
    if (error) {
      const errorParts = [
        `🌍 Domain: ${currentDomain}`,
        `🌐 URL: ${window.location.href}`,
        `🕒 Date: ${currentDateTime}`,
      ];

      if (appVersion) errorParts.push(`🛠 App version: ${appVersion}`);
      if (apiVersion) errorParts.push(`🧩 API version: ${apiVersion}`);
      if (userId) errorParts.push(`👤 User ID: ${userId}`);

      errorParts.push('');
      errorParts.push('--- Error Stack ---');
      errorParts.push(error.stack || error.message);

      const fullErrorText = errorParts.join('\n');

      await navigator.clipboard.writeText(fullErrorText);
    }
  };

  // Отдельный UI для ChunkLoadError
  if (isChunkLoadError) {
    return (
      <div className={cn(styles.pageErrorWrapper)}>
        <div className={cn(styles.pageErrorContent)}>
          <h1 className={cn(styles.pageErrorTitle)}>
            🔄 {t('exceptions.newVersionAvailable') || 'Доступна новая версия'}
          </h1>

          <Alert
            message={t('exceptions.chunkLoadError.title') || 'Требуется обновление страницы'}
            description={
              t('exceptions.chunkLoadError.description') ||
              'Мы выпустили новую версию приложения. Пожалуйста, перезагрузите страницу, чтобы продолжить работу.'
            }
            type="info"
            showIcon
            style={{ maxWidth: '600px', marginBottom: '20px' }}
          />

          <Button type="primary" size="large" icon={<ReloadOutlined />} onClick={reloadPage}>
            {t('buttons.reloadPage') || 'Перезагрузить страницу'}
          </Button>

          <Collapse style={{ marginTop: '20px', maxWidth: '600px' }} ghost>
            <Collapse.Panel header={t('exceptions.reports.technicalDetails') || 'Технические детали'} key="1">
              <p className={cn(styles.pageErrorInfoItem)}>
                {`${t('exceptions.reports.currentDomain') || 'Домен'}: ${currentDomain}`} <br />
                {`${t('exceptions.reports.date') || 'Время'}: ${currentDateTime}`}
              </p>
              <pre className={cn(styles.errorStack)}>{error.stack || error.message}</pre>
            </Collapse.Panel>
          </Collapse>
        </div>
      </div>
    );
  }

  // Обычный UI для других ошибок
  return (
    <div className={cn(styles.pageErrorWrapper)}>
      <div className={cn(styles.pageErrorContent)}>
        <h1 className={cn(styles.pageErrorTitle)}>{t('exceptions.somethingWrong') || 'Something went wrong'}</h1>
        <p className={cn(styles.pageErrorInfoItem)}>
          {`${t('exceptions.reports.currentDomain') || 'Current domain'}: ${currentDomain}`} <br />
          {`${t('exceptions.reports.currentUrl') || 'Current url'}: ${window.location.href}`} <br /> <br />
          {`${t('exceptions.reports.date') || 'Current date'}: ${currentDateTime || 'not found'}`}
        </p>

        {error && (
          <div className={cn(styles.errorDetails)}>
            <Collapse
              activeKey={expanded ? ['1'] : []}
              defaultActiveKey={['1']}
              onChange={() => setExpanded(!expanded)}
            >
              <Collapse.Panel header={t('exceptions.reports.details') || 'details'} key="1">
                {(appVersion || apiVersion || userId) && (
                  <div className={cn(styles.hiddenErrorInfo)}>
                    {appVersion && (
                      <p>{`${t('exceptions.reports.appVersion') || 'App version'}: ${appVersion || 'not found'}`}</p>
                    )}
                    {apiVersion && (
                      <p>{`${t('exceptions.reports.apiVersion') || 'Api version'}: ${apiVersion || 'not found'}`}</p>
                    )}
                    {userId && <p>{`${t('exceptions.reports.userId') || 'User ID'}: ${userId || 'not found'}`}</p>}
                  </div>
                )}
                <pre className={cn(styles.errorStack)}>{error.stack || error.message}</pre>
                <div className={cn(styles.errorDetailsContent)}>
                  <Button icon={<CopyOutlined />} onClick={copyError}>
                    {t('buttons.copyError') || 'Copy error'}
                  </Button>
                  <p className={cn(styles.sendReportLabel)}>
                    {`🙏 ${t('exceptions.reports.reportErrorTo') || 'Send error message to'}: support@bpium.ru`}
                  </p>
                </div>
              </Collapse.Panel>
            </Collapse>
          </div>
        )}
        <Button onClick={reloadPage}>{t('buttons.reloadPage') || 'Reload page'}</Button>
      </div>
    </div>
  );
};

export default connect(
  PageError,
  {
    company: ['company'],
    user: ['user'],
  },
  (props, { company, user }) => ({
    company,
    user,
    ...props,
  }),
);
